import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Button, Chip, Grid, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getMasters,
  getStateReference,
  getStateUsage,
  type StateSubdivision,
} from '../../services/platform/masterService';
import {
  STATE_FIELD_LABELS,
  STATE_MAX_SORT_ORDER,
  countryDisplayName,
  countryFlag,
  nextStateSortOrder,
  normalizeStateName,
  previewStateCode,
  validateStateForm,
  type StateExistingRecord,
} from '../../utils/stateMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

interface CountryOption {
  code: string;
  name: string;
  isActive: boolean;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());

function describeUsage(u: { cities: number; zones: number; dispatchZones: number; addresses: number }) {
  const parts: string[] = [];
  if (u.cities) parts.push(`${u.cities} cit${u.cities === 1 ? 'y' : 'ies'}`);
  if (u.zones) parts.push(`${u.zones} operational zone${u.zones === 1 ? '' : 's'}`);
  if (u.dispatchZones) parts.push(`${u.dispatchZones} dispatch zone${u.dispatchZones === 1 ? '' : 's'}`);
  if (u.addresses) parts.push(`${u.addresses} saved address${u.addresses === 1 ? '' : 'es'}`);
  return parts.join(', ');
}

export default function StateFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const country = str(form.country).toUpperCase();
  const meta = form.metadata;
  const isoCode = str(meta.isoCode).toUpperCase();

  const { data: countriesRaw, isLoading: countriesLoading } = useQuery({
    queryKey: ['masters', 'countries', 'state-form'],
    queryFn: () => getMasters('countries', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const countryOptions = useMemo<CountryOption[]>(
    () => ((countriesRaw?.data ?? []) as Array<{ code?: string; name?: string; isActive?: boolean }>)
      .map((c) => ({ code: str(c.code).toUpperCase(), name: str(c.name), isActive: c.isActive !== false }))
      .filter((c) => /^[A-Z]{2}$/.test(c.code))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [countriesRaw],
  );
  const selectedCountry = useMemo<CountryOption | null>(() => {
    if (!country) return null;
    return countryOptions.find((c) => c.code === country)
      || { code: country, name: countryDisplayName(country), isActive: true };
  }, [country, countryOptions]);

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['state-reference', country],
    queryFn: () => getStateReference(country),
    enabled: open && !!country,
    staleTime: Infinity,
  });
  const { data: siblingsRaw, isFetching: siblingsLoading } = useQuery({
    queryKey: ['masters', 'states', 'country', country],
    queryFn: () => getMasters('states', { country, limit: 2000, page: 1 }),
    enabled: open && !!country,
    staleTime: 0,
  });
  const siblings = useMemo<StateExistingRecord[]>(
    () => ((siblingsRaw?.data ?? []) as StateExistingRecord[]),
    [siblingsRaw],
  );
  const { data: usage } = useQuery({
    queryKey: ['state-usage', editingId],
    queryFn: () => getStateUsage(editingId!),
    enabled: open && editing && !!editingId,
    staleTime: 0,
  });

  const takenCodes = useMemo(
    () => new Set(siblings.filter((r) => r._id !== editingId).map((r) => str(r.code).toUpperCase())),
    [siblings, editingId],
  );
  const subdivisions = reference?.subdivisions ?? [];
  const selectedSubdivision = useMemo(
    () => subdivisions.find((s) => s.isoCode === isoCode) || null,
    [subdivisions, isoCode],
  );
  const suggestedSortOrder = useMemo(() => nextStateSortOrder(siblings, editingId), [siblings, editingId]);
  const effectiveCode = editing
    ? str(form.code).toUpperCase()
    : previewStateCode(country, form.name, selectedSubdivision?.code, takenCodes);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) onChange({ ...form, isActive: true, code: '', sortOrder: 0 });
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create') return;
    const patch: Partial<MasterFormState> = {};
    if (effectiveCode !== str(form.code)) patch.code = effectiveCode;
    if (country && !siblingsLoading && !touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      patch.sortOrder = suggestedSortOrder;
    }
    if (Object.keys(patch).length) onChange({ ...form, ...patch });
  }, [open, editing, effectiveCode, country, siblingsLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateStateForm(
      { country, name: form.name, code: effectiveCode, description: form.description, sortOrder: form.sortOrder },
      { existing: siblings, editingId },
    ),
    [country, form.name, effectiveCode, form.description, form.sortOrder, siblings, editingId],
  );
  const blocking = Object.keys(errors).map((key) => [key, STATE_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !siblingsLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const applyCountry = (option: CountryOption | null) => {
    onChange({ ...form, country: option?.code || '', name: '', code: '', sortOrder: 0, metadata: {} });
    setTouched(new Set(['country']));
  };
  const applySubdivision = (sub: StateSubdivision | null) => {
    onChange({
      ...form,
      name: sub ? sub.name : form.name,
      metadata: sub
        ? { isoCode: sub.isoCode, latitude: sub.latitude ?? '', longitude: sub.longitude ?? '' }
        : {},
    });
    touch('subdivision', 'name');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const usedBy = usage && usage.total > 0 ? describeUsage(usage) : '';
  const officialCount = subdivisions.length;
  const remaining = subdivisions.filter((s) => !takenCodes.has(s.code)).length;

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} State / Province`}
      subtitle="Pick the country, then choose an official subdivision — the name, ISO 3166-2 code and coordinates fill in automatically."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={editing ? 'Update' : 'Create'}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${editing ? 'update' : 'create'} — see the checklist at the top`
        : undefined}
      width={640}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={country ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!country}>
            {country
              ? `Complete these to enable ${editing ? 'Update' : 'Create'}:`
              : 'Start by selecting a country.'}
          </Typography>
          {country && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip key={key} size="small" label={label} color="warning" variant="outlined" onClick={() => focusField(key)} />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {editing && (
        <Alert severity={usedBy ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {usedBy
            ? `Linked to ${usedBy}. Country and code are locked so these links stay intact.`
            : 'Country and code are locked to keep this state / province linked to its country.'}
        </Alert>
      )}

      <FormSection title="Location" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="country">
            <Autocomplete
              options={countryOptions}
              loading={countriesLoading}
              disabled={editing}
              value={selectedCountry}
              onChange={(_, value) => applyCountry(value)}
              onBlur={() => touch('country')}
              getOptionLabel={(o) => `${countryFlag(o.code)} ${o.name} (${o.code})`.trim()}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              getOptionDisabled={(o) => !o.isActive}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.code}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <span>{countryFlag(o.code)}</span>
                    <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.code}</Typography>
                    {!o.isActive && <Chip size="small" label="Inactive" />}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Country"
                  placeholder="Search country"
                  error={!!errorFor('country')}
                  helperText={errorFor('country')
                    || (editing ? 'Country cannot be changed after creation' : 'Only active countries from the Countries master')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="subdivision">
            <Autocomplete
              options={subdivisions}
              loading={referenceLoading || siblingsLoading}
              disabled={editing || !country}
              value={selectedSubdivision}
              onChange={(_, value) => applySubdivision(value)}
              getOptionLabel={(o) => `${o.name} (${o.code})`}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              getOptionDisabled={(o) => takenCodes.has(o.code)}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => o.name.toLowerCase().includes(q)
                  || o.isoCode.toLowerCase() === q
                  || o.code.toLowerCase() === q);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.code}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">{o.code}</Typography>
                    {takenCodes.has(o.code) && <Chip size="small" label="Already added" />}
                  </Stack>
                </li>
              )}
              noOptionsText={country ? 'No official subdivisions — type the name below' : 'Select a country first'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Official subdivision"
                  placeholder={country ? 'Search by name or code' : 'Select a country first'}
                  helperText={editing
                    ? 'Locked after creation'
                    : country && officialCount
                      ? `${remaining} of ${officialCount} not added yet · leave empty for a custom region`
                      : 'Optional — leave empty to add a custom region'}
                />
              )}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Details" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="State / Province name"
              value={form.name}
              disabled={!country}
              onChange={(e) => {
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeStateName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Must be unique within the country'}
              inputProps={{ maxLength: 80 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="code">
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={effectiveCode}
              placeholder={country ? `${country}-…` : ''}
              error={!!errorFor('code') || (!!errors.code && !!effectiveCode)}
              helperText={(touched.has('code') || effectiveCode ? errors.code : undefined)
                || (editing
                  ? 'Codes cannot be changed after creation'
                  : selectedSubdivision
                    ? 'Auto-generated — ISO 3166-2 code of the subdivision'
                    : 'Auto-generated from the name (unique within the country)')}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="Auto" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              disabled={!country}
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errors.sortOrder && !!country && (touched.has('sortOrder') || /already used/.test(errors.sortOrder))}
              helperText={
                country && errors.sortOrder && (touched.has('sortOrder') || /already used/.test(errors.sortOrder))
                  ? errors.sortOrder
                  : country
                    ? `Unique within ${selectedCountry?.name || country} — next available is ${suggestedSortOrder}`
                    : 'Suggested after you pick a country'
              }
              inputProps={{ min: 1, max: STATE_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: country && form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the list to activate or deactivate this state / province.'
                : 'New states / provinces are created as Active. Use the status toggle in the list to deactivate.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          {(str(meta.latitude) || str(meta.longitude)) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Centre point: {Number(meta.latitude).toFixed(4)}, {Number(meta.longitude).toFixed(4)}
              </Typography>
            </Grid>
          )}
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
